import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import onClickOutside from "react-onclickoutside";
import Colors from "Colors";
import SvgIcon from "Controls/SvgIcon";

// A compact language picker matching the check-in prototype (24bbe229):
// a white, bordered, rounded trigger showing the current flag + language name
// + chevron, opening a popover list of flag + name rows. Visuals mirror
// ComponentsLibrary DropdownSelectHeader so it fits the design system.

const Wrapper = styled.div`
	position: relative;
	display: inline-block;
	flex-shrink: 0;
`;

const Trigger = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	height: 40px;
	padding: 0 12px;
	background-color: ${Colors.white};
	border: 1px solid ${Colors.metal};
	border-radius: 6px;
	color: ${Colors.onyx};
	font-size: 14px;
	white-space: nowrap;
	cursor: pointer;
	&:hover {
		border-color: ${Colors.buttonLightGray};
	}
`;

const FlagImg = styled.img`
	width: 20px;
	height: auto;
	display: block;
`;

const CurrentName = styled.span`
	line-height: 1;
`;

const Popover = styled.ul`
	position: absolute;
	top: calc(100% + 4px);
	right: 0;
	min-width: 100%;
	margin: 0;
	padding: 4px 0;
	list-style: none;
	background-color: ${Colors.white};
	border: 1px solid ${Colors.fog};
	border-radius: 6px;
	box-shadow: 0 4px 12px ${Colors.blackAlpha12};
	z-index: 1000;
	/* Cap the height so a long language list scrolls inside the popover
	   instead of stretching the page (WIT-12576). */
	max-height: 60vh;
	overflow-y: auto;
`;

const Item = styled.li`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	color: ${Colors.onyx};
	font-size: 14px;
	white-space: nowrap;
	cursor: pointer;
	background-color: ${(props) => (props.selected ? Colors.lightTurquoise : "transparent")};
	&:hover {
		background-color: ${Colors.lightTurquoise};
	}
`;

class CheckinLanguageSelect extends Component {
	constructor(props) {
		super(props);

		this.state = { open: false };
	}

	handleClickOutside = () => {
		if (this.state.open) {
			this.setState({ open: false });
		}
	};

	toggle = () => {
		this.setState((state) => ({ open: !state.open }));
	};

	select = (culture) => {
		this.setState({ open: false });
		if (culture !== this.props.culture) {
			this.props.onCultureChange(culture);
		}
	};

	render() {
		const { languages, culture } = this.props;
		const current = languages.find((l) => l.culture === culture) || languages[0];

		if (!current) {
			return null;
		}

		return (
			<Wrapper>
				<Trigger type="button" onClick={this.toggle} aria-label="Language">
					<FlagImg src={current.imageUrl} alt="" />
					<CurrentName>{current.name}</CurrentName>
					<SvgIcon icon="chevron-down-16" size="16px" />
				</Trigger>
				{this.state.open && (
					<Popover>
						{languages.map((language) => (
							<Item
								key={language.culture}
								selected={language.culture === culture}
								onClick={() => this.select(language.culture)}>
								<FlagImg src={language.imageUrl} alt="" />
								<span>{language.name}</span>
							</Item>
						))}
					</Popover>
				)}
			</Wrapper>
		);
	}
}

CheckinLanguageSelect.propTypes = {
	culture: PropTypes.string,
	languages: PropTypes.array,
	onCultureChange: PropTypes.func,
};

export default onClickOutside(CheckinLanguageSelect);
